import { type FormEvent, useState } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { signInErrorMessage } from '../auth/session.js';
import { EmailDeliveryHint } from '../components/EmailDeliveryHint.js';

/** Minimum length of the passwords chosen by external users (docs/adr/ADR-004). */
const MIN_PASSWORD_LENGTH = 12;

type View = 'sign-in' | 'first-access' | 'reset';

export function SignInPage() {
  const { config } = useAuth();
  const [view, setView] = useState<View>('sign-in');

  return (
    <main className="centered">
      <section className="card sign-in">
        <img className="sign-in-logo" src="/k-rev-logo.png" alt="K-Rev" width={129} height={56} />
        <h1>K-Management</h1>
        {config?.mode === 'development' ? (
          <DevelopmentSignIn />
        ) : view === 'sign-in' ? (
          <SignIn
            passwordSignIn={config?.mode === 'firebase' && config.passwordSignIn}
            onView={setView}
          />
        ) : view === 'first-access' ? (
          <FirstAccess onView={setView} />
        ) : (
          <PasswordReset onView={setView} />
        )}
      </section>
    </main>
  );
}

function SignIn({
  passwordSignIn,
  onView,
}: {
  passwordSignIn: boolean;
  onView: (view: View) => void;
}) {
  const { session } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(signInErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void run(() => session!.signInWithPassword(email, password));
  };

  return (
    <div className="stack">
      <p className="muted">Gestionale di K-Rev</p>
      <button
        type="button"
        className="primary wide"
        disabled={busy || !session}
        onClick={() => void run(() => session!.signInWithMicrosoft())}
      >
        Accedi con Microsoft 365
      </button>
      <small className="muted">Per chi ha un account K-Rev.</small>

      {passwordSignIn && (
        <>
          <div className="separator">
            <span>oppure, se non hai un account K-Rev</span>
          </div>
          <form onSubmit={submit} className="stack">
            <label>
              E-mail
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              Password
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <button type="submit" className="wide" disabled={busy || !session}>
              Accedi con e-mail
            </button>
          </form>
          <div className="link-row">
            <button type="button" className="link" onClick={() => onView('first-access')}>
              Primo accesso
            </button>
            <button type="button" className="link" onClick={() => onView('reset')}>
              Password dimenticata
            </button>
          </div>
        </>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function FirstAccess({ onView }: { onView: (view: View) => void }) {
  const { session, config } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const internalDomains = config?.mode === 'firebase' ? config.internalEmailDomains : [];

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const domain = email.trim().toLowerCase().split('@')[1] ?? '';
    if (internalDomains.includes(domain)) {
      setError(`Gli account ${domain} accedono con «Accedi con Microsoft 365».`);
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`La password deve avere almeno ${MIN_PASSWORD_LENGTH} caratteri.`);
      return;
    }
    if (password !== confirm) {
      setError('Le due password non coincidono.');
      return;
    }
    setBusy(true);
    try {
      // On success the account is signed in and the app shows the verification page.
      await session!.createPasswordAccount(email, password);
    } catch (failure) {
      setError(signInErrorMessage(failure));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="stack">
      <h2>Primo accesso</h2>
      <p className="muted">
        Usa l’e-mail con cui l’amministratore ti ha abilitato e scegli una password di almeno{' '}
        {MIN_PASSWORD_LENGTH} caratteri. Riceverai un’e-mail per confermare l’indirizzo. Se hai già
        creato l’accesso in passato, torna ad «Accedi» o usa «Password dimenticata».
      </p>
      <EmailDeliveryHint />
      <label>
        E-mail
        <input
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      <label>
        Password
        <input
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>
      <label>
        Ripeti la password
        <input
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
      </label>
      {error && <p className="error">{error}</p>}
      <button type="submit" className="primary wide" disabled={busy}>
        Crea l’accesso
      </button>
      <button type="button" className="link" onClick={() => onView('sign-in')}>
        Torna all’accesso
      </button>
    </form>
  );
}

function PasswordReset({ onView }: { onView: (view: View) => void }) {
  const { session } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await session!.sendPasswordReset(email);
      setSent(true);
    } catch (failure) {
      const code = (failure as { code?: string }).code;
      // Never reveal whether an account exists for the address.
      if (code === 'auth/user-not-found') setSent(true);
      else setError(signInErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="stack">
      <h2>Password dimenticata</h2>
      {sent ? (
        <>
          <p className="notice info">
            Se esiste un accesso per <strong>{email}</strong> ti abbiamo inviato un’e-mail con il
            link per scegliere una nuova password.
          </p>
          <p className="muted small">
            Non arriva nulla se l’accesso non è mai stato creato: in quel caso usa «Primo accesso».
          </p>
          <EmailDeliveryHint />
        </>
      ) : (
        <>
          <p className="muted">
            Vale solo se hai già fatto il «Primo accesso». Se non l’hai mai fatto, usa quello.
          </p>
          <label>
            E-mail
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" className="primary wide" disabled={busy}>
            Invia il link
          </button>
        </>
      )}
      <button type="button" className="link" onClick={() => onView('sign-in')}>
        Torna all’accesso
      </button>
    </form>
  );
}

function DevelopmentSignIn() {
  const { session } = useAuth();
  const [email, setEmail] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void session?.signInWithPassword(email, '');
  };

  return (
    <form onSubmit={submit} className="stack">
      <p className="notice">Modalità sviluppo: accesso senza password con l’e-mail di un utente.</p>
      <label>
        E-mail
        <input
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="alessandro.collin@k-rev.com"
        />
      </label>
      <button type="submit" className="primary wide" disabled={!session}>
        Accedi
      </button>
    </form>
  );
}
